import { useState, useEffect, useCallback } from 'react';

interface DismissedBanner {
  message: string;
  dismissedAt: number;
  endsAt?: number;
}

const STORAGE_KEY = 'dismissed-banners';
const MAX_STORAGE_ENTRIES = 50;
const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000;

export function useDismissibleBanner(message: string, endsAt?: Date) {
  const [isDismissed, setIsDismissed] = useState(false);

  const getBannerKey = useCallback(() => {
    return `${message}_${endsAt?.getTime() || 'permanent'}`;
  }, [message, endsAt]);

  const cleanupStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const dismissedBanners: Record<string, DismissedBanner> =
        JSON.parse(stored);
      const now = Date.now();
      const cleaned: Record<string, DismissedBanner> = {};
      let hasChanges = false;

      Object.entries(dismissedBanners).forEach(([key, banner]) => {
        const shouldKeep =
          now - banner.dismissedAt < CLEANUP_INTERVAL &&
          (!banner.endsAt || banner.endsAt > now);

        if (shouldKeep) {
          cleaned[key] = banner;
        } else {
          hasChanges = true;
        }
      });

      const entries = Object.entries(cleaned);
      if (entries.length > MAX_STORAGE_ENTRIES) {
        const sorted = entries.sort(
          (a, b) => b[1].dismissedAt - a[1].dismissedAt
        );
        const trimmed = sorted.slice(0, MAX_STORAGE_ENTRIES);
        Object.keys(cleaned).forEach((key) => {
          if (!trimmed.find(([k]) => k === key)) {
            delete cleaned[key];
            hasChanges = true;
          }
        });
      }

      if (hasChanges) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
      }
    } catch (error) {
      console.warn('Failed to cleanup banner storage:', error);
    }
  }, []);

  const checkDismissed = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return false;

      const dismissedBanners: Record<string, DismissedBanner> =
        JSON.parse(stored);
      const bannerKey = getBannerKey();
      const dismissedBanner = dismissedBanners[bannerKey];

      if (!dismissedBanner) return false;

      if (dismissedBanner.endsAt && dismissedBanner.endsAt <= Date.now()) {
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Failed to check dismissed banner:', error);
      return false;
    }
  }, [getBannerKey]);

  const dismissBanner = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const dismissedBanners: Record<string, DismissedBanner> = stored
        ? JSON.parse(stored)
        : {};

      const bannerKey = getBannerKey();
      dismissedBanners[bannerKey] = {
        message,
        dismissedAt: Date.now(),
        endsAt: endsAt?.getTime(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissedBanners));
      setIsDismissed(true);
    } catch (error) {
      console.warn('Failed to dismiss banner:', error);
    }
  }, [message, endsAt, getBannerKey]);

  useEffect(() => {
    cleanupStorage();
    setIsDismissed(checkDismissed());
  }, [cleanupStorage, checkDismissed]);

  return {
    isDismissed,
    dismissBanner,
  };
}
